import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertTriangle, CheckCircle2, Database, RefreshCw } from "lucide-react";

const errText = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Something went wrong."; };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" }) : "—");
const btn = "text-sm px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 transition-colors disabled:opacity-50";
const btnDark = "text-sm px-4 py-2 rounded-lg bg-slate-900 text-white hover:bg-slate-700 transition-colors disabled:opacity-40";

function Step({ n, title, done, testid, children }) {
  return (
    <section className="pf-card p-5" data-testid={testid}>
      <h3 className="font-medium text-slate-900 flex items-center gap-2">
        {done ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <span className="w-6 h-6 rounded-full bg-slate-900 text-white text-xs flex items-center justify-center">{n}</span>}
        {title}
      </h3>
      <div className="mt-3 text-sm text-slate-700 space-y-3">{children}</div>
    </section>
  );
}

function SourceCard({ s }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="pf-card p-5" data-testid="dbmove-source">
      <h3 className="font-medium text-slate-900 flex items-center gap-2"><Database className="w-4 h-4" /> Database the live site is using now</h3>
      <dl className="grid sm:grid-cols-3 gap-3 mt-3 text-sm">
        <div><dt className="text-xs text-slate-500">Server</dt><dd className="break-all" data-testid="dbmove-source-host">{s.source.host}</dd></div>
        <div><dt className="text-xs text-slate-500">Database name</dt><dd data-testid="dbmove-source-db">{s.source.db_name}</dd></div>
        <div><dt className="text-xs text-slate-500">Records</dt><dd data-testid="dbmove-source-total">{s.total.toLocaleString()} in {s.collections.length} collections</dd></div>
      </dl>
      {s.source.using_test_db && (
        <p className="mt-3 text-sm text-amber-800 bg-amber-50 rounded-lg p-3 flex gap-2" data-testid="dbmove-test-db-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> The site is in test-database mode (USE_TEST_DB is on), so it ignores MONGO_URL. When you switch, also set USE_TEST_DB to false.
        </p>)}
      <button type="button" className="text-xs underline mt-3" onClick={() => setOpen(!open)} data-testid="dbmove-toggle-counts">{open ? "Hide" : "Show"} record counts</button>
      {open && (
        <table className="w-full text-xs mt-2" data-testid="dbmove-counts-table"><tbody>
          {s.collections.map((c) => <tr key={c.name} className="border-t"><td className="py-1">{c.name}</td><td className="text-right">{c.count.toLocaleString()}</td></tr>)}
        </tbody></table>)}
    </section>
  );
}

function PauseStep({ freeze, running, reload }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const toggle = async () => {
    if (!freeze.enabled && !window.confirm("Pause all edits on the live site? Visitors can still browse, but sign-ups, coupon claims, checkout coupon checks and merchant changes wait until edits reopen.")) return;
    setBusy(true); setErr(null);
    try { await api.post("/admin/db-move/freeze", { enabled: !freeze.enabled }); await reload(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Step n={1} title="Pause edits" done={freeze.enabled} testid="dbmove-step-pause">
      <p>While edits are paused nothing new is saved, so the copy is complete. Visitors can still browse; sign-ups, coupon claims, checkout coupon checks, merchant changes and background jobs wait.</p>
      <p className="font-medium" data-testid="dbmove-freeze-status">
        {freeze.enabled ? `Edits paused since ${fmt(freeze.changed_at)}` : "Edits are open."}
        {freeze.enabled && freeze.settle_seconds_left > 0 && ` · You can copy in ${freeze.settle_seconds_left}s`}
      </p>
      <button type="button" className={freeze.enabled ? btn : btnDark} disabled={busy || running} onClick={toggle} data-testid="dbmove-freeze-toggle">
        {freeze.enabled ? "Reopen edits" : "Pause edits"}
      </button>
      {err && <p className="text-rose-600" data-testid="dbmove-freeze-error">{err}</p>}
    </Step>
  );
}

function TargetStep({ target, setTarget, check, setCheck }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const edit = (k) => (e) => { setTarget({ ...target, [k]: e.target.value }); setCheck(null); };
  const run = async () => {
    setBusy(true); setErr(null);
    try { setCheck((await api.post("/admin/db-move/check", target)).data); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Step n={2} title="Connect the new database" done={!!check} testid="dbmove-step-target">
      <p>Paste the full Atlas connection string, including the password. It is used only for this check and the copy, and is never saved or logged.</p>
      <p className="text-xs text-slate-500">In Atlas → Network Access, allow every address listed at <a className="underline" href="https://app.emergent.sh/ip-addresses" target="_blank" rel="noreferrer" data-testid="dbmove-ip-link">app.emergent.sh/ip-addresses</a>.</p>
      <div className="grid sm:grid-cols-[1fr_12rem] gap-2">
        <input type="password" autoComplete="off" spellCheck={false} value={target.uri} onChange={edit("uri")} placeholder="mongodb+srv://user:password@cluster.mongodb.net/" className="border rounded-lg px-3 py-2" data-testid="dbmove-target-uri" />
        <input value={target.db_name} onChange={edit("db_name")} placeholder="Database name" className="border rounded-lg px-3 py-2" data-testid="dbmove-target-db" />
      </div>
      <button type="button" className={btn} disabled={busy || target.uri.length < 10 || !target.db_name} onClick={run} data-testid="dbmove-check-btn">{busy ? "Checking…" : "Check connection"}</button>
      {check && (
        <p className={check.empty ? "text-emerald-700" : "text-amber-700"} data-testid="dbmove-check-result">
          Connected to {check.host} · database {check.db_name} · {check.empty ? "empty and ready" : `${check.documents.toLocaleString()} records already inside`}
        </p>)}
      {err && <p className="text-rose-600" data-testid="dbmove-check-error">{err}</p>}
    </Step>
  );
}

function RunStatus({ run }) {
  if (!run) return null;
  const v = run.verification;
  return (
    <div className="rounded-lg bg-slate-50 p-3 space-y-1" data-testid="dbmove-run">
      <p data-testid="dbmove-run-status">
        <strong>{{ running: "Copying…", verified: "Copy complete and verified", mismatch: "Copy finished, but counts differ", failed: "Copy failed" }[run.status]}</strong>
        {" "}· {run.done.length}/{run.total_collections} collections · into {run.target_db} on {run.target_host} · started {fmt(run.started_at)}
      </p>
      {v && <p data-testid="dbmove-run-totals">Records: {v.source_total.toLocaleString()} in the live database, {v.target_total.toLocaleString()} in the new one.</p>}
      {v && v.mismatches.length > 0 && (
        <div data-testid="dbmove-run-mismatches">
          <p className="text-amber-700">Some records changed during the copy. Run the copy again with “skip records already copied” ticked; it only adds what is missing.</p>
          <ul className="text-xs">{v.mismatches.map((m) => <li key={m.name}>{m.name}: {m.source} live, {m.target} new</li>)}</ul>
        </div>)}
      {run.error && <p className="text-rose-600" data-testid="dbmove-run-error">{run.error}</p>}
    </div>
  );
}

function CopyStep({ freeze, target, check, run, reload }) {
  const [allow, setAllow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const running = run?.status === "running";
  const ready = freeze.enabled && !freeze.settle_seconds_left && check && !running && (check.empty || allow);
  const start = async () => {
    if (!window.confirm(`Copy every collection into "${target.db_name}" on ${check.host}?`)) return;
    setBusy(true); setErr(null);
    try { await api.post("/admin/db-move/copy", { ...target, allow_existing: allow, confirm: "COPY" }); await reload(); } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  return (
    <Step n={3} title="Copy all data" done={run?.status === "verified"} testid="dbmove-step-copy">
      <p>Copies every collection with its indexes, then checks that each record count matches.</p>
      {check && !check.empty && (
        <label className="flex items-center gap-2"><input type="checkbox" checked={allow} onChange={(e) => setAllow(e.target.checked)} data-testid="dbmove-allow-existing" />
          Skip records already copied (only if the existing data came from an earlier copy attempt)</label>)}
      <button type="button" className={btnDark} disabled={!ready || busy} onClick={start} data-testid="dbmove-copy-btn">{busy ? "Starting…" : "Copy all data"}</button>
      {!freeze.enabled && <p className="text-xs text-slate-500">Pause edits first.</p>}
      <RunStatus run={run} />
      {err && <p className="text-rose-600" data-testid="dbmove-copy-error">{err}</p>}
    </Step>
  );
}

function SwitchStep({ run, usingTestDb }) {
  const name = run?.target_db || "cyannectinc";
  return (
    <Step n={4} title="Switch the live site" testid="dbmove-step-switch">
      <ol className="list-decimal pl-5 space-y-1.5">
        <li>In Emergent, open <strong>Manage Publishes → Secrets → System keys</strong>.</li>
        <li>Set <code>MONGO_URL</code> to the same connection string, and <code>DB_NAME</code> to <code>{name}</code>.</li>
        <li className={usingTestDb ? "text-amber-800 font-medium" : ""}>Set <code>USE_TEST_DB</code> to <code>false</code>.</li>
        <li>Redeploy normally. Do <strong>not</strong> choose “New DB”; that copies preview test data into your database.</li>
        <li>After the deploy, edits reopen by themselves (the pause stays only in the old database). Reload this page and check the server above shows your new cluster.</li>
        <li>If the deploy fails, come back here and press “Reopen edits”.</li>
      </ol>
    </Step>
  );
}

export default function DatabaseMove() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(null);
  const [target, setTarget] = useState({ uri: "", db_name: "cyannectinc" });
  const [check, setCheck] = useState(null);
  const load = useCallback(() => api.get("/admin/db-move/status").then((r) => { setS(r.data); setErr(null); }).catch((e) => setErr(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  const running = s?.run?.status === "running";
  const settling = s?.freeze?.enabled && s.freeze.settle_seconds_left > 0;
  useEffect(() => {
    if (!running && !settling) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [running, settling, load]);
  return (
    <div className="max-w-3xl space-y-4" data-testid="dbmove-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="pf-chip pf-chip-cyan"><Database className="w-3 h-3" /> Super admin</div>
          <h2 className="font-display text-3xl font-medium tracking-tight mt-3 text-slate-900">Move database</h2>
          <p className="text-sm text-slate-600 mt-1">Copy the live data into a new MongoDB before pointing the site at it.</p>
        </div>
        <button type="button" onClick={load} className="pf-btn text-sm flex items-center gap-1" data-testid="dbmove-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>
      {err && <p className="text-sm text-rose-600" data-testid="dbmove-error">{err}</p>}
      {s && (<>
        <SourceCard s={s} />
        <PauseStep freeze={s.freeze} running={running} reload={load} />
        <TargetStep target={target} setTarget={setTarget} check={check} setCheck={setCheck} />
        <CopyStep freeze={s.freeze} target={target} check={check} run={s.run} reload={load} />
        <SwitchStep run={s.run} usingTestDb={s.source.using_test_db} />
      </>)}
    </div>
  );
}
